import { Fragment, useEffect, useRef } from "react";
import { Icon } from "@/components/Icon/Icon";
import { Pill } from "@/components/primitives";
import type { Target } from "@/utils/types/flavor";

type FlavorDetailPopoverProps = {
  target: Target | null | undefined;
  anchorRect: DOMRect | null;
  anchorEl: HTMLElement | null;
  onChangeTarget?: () => void;
  onClose: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
};

export function FlavorDetailPopover({
  target,
  anchorRect,
  anchorEl,
  onChangeTarget,
  onClose,
  onMouseEnter,
  onMouseLeave,
}: FlavorDetailPopoverProps) {
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (!ref.current) return;
      if (ref.current.contains(e.target as Node)) return;
      if (anchorEl && anchorEl.contains(e.target as Node)) return;
      onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [anchorEl, onClose]);

  // Anchor: right-edge aligned under the info icon, with a small offset.
  const top = anchorRect ? anchorRect.bottom + 8 : 56;
  const right = anchorRect
    ? Math.max(12, window.innerWidth - anchorRect.right)
    : 24;

  const rows = (target && target.constraints) || [];

  return (
    <div
      ref={ref}
      className="detail-popover slide-up"
      style={{ top, right }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div className="detail-popover-header">
        <div className="hstack gap-2">
          <Icon name="target" size={13} />
          <span className="label">Target flavor</span>
        </div>
        <div className="hstack gap-2">
          {onChangeTarget && (
            <button type="button" className="btn xs ghost" onClick={onChangeTarget}>
              Change
            </button>
          )}
          <button type="button" className="btn xs ghost" onClick={onClose}>
            <Icon name="x" size={11} />
          </button>
        </div>
      </div>

      {!target ? (
        <div className="detail-popover-empty">
          No target attached. You're working from scratch.
        </div>
      ) : (
        <div className="detail-popover-body">
          <div className="display detail-popover-name">{target.name}</div>
          <Pill family={target.family} />
          {target.descriptor && (
            <div className="detail-popover-descriptor">{target.descriptor}</div>
          )}

          {rows.length > 0 && (
            <div className="detail-popover-constraints">
              <div className="hstack" style={{ justifyContent: "space-between", marginBottom: 8 }}>
                <span className="label" style={{ margin: 0 }}>
                  Constraints
                </span>
                <span className="constraint-count-pill">{rows.length}</span>
              </div>
              <div className="detail-popover-constraints-grid">
                {rows.map((r) => (
                  <Fragment key={r.key}>
                    <div className="label">{r.label}</div>
                    <div className="row-value">{r.value}</div>
                  </Fragment>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
